import type { ReactNode } from "react";
import { CopyableEmail } from "@/components/common/CopyableEmail";
import { ArrowRight } from "lucide-react";
import SlideShell, { Bullets, Body, Callout, Grad, Lead, Title } from "./components/SlideShell";

const ASSET = (name: string) => `/tour-assets/${name}`;

export const TOTAL_SLIDES = 21;

const DEMO_PHONE = "+64 9 886 4856";
const DEMO_WEB_PATH = "/live-agent/business-onboarding";
const DEMO_WEB_DISPLAY = "helpgenie.ai/live-agent/business-onboarding";
const DEMO_WEB_HREF = `https://helpgenie.ai${DEMO_WEB_PATH}`;
const FOUNDER_EMAIL = "sam@helpgenie.ai";
const DOMAIN = "helpgenie.ai";

// ─── Slide 1 — Cover (image-led, full-bleed) ────────────────────────────────
const Cover = ({ onStart }: { onStart: () => void }) => (
  <div className="relative h-full w-full overflow-hidden">
    <img
      src={ASSET("usecase-intake.webp")}
      alt="A modern office scene with a branded Help Genie running on a customer's phone"
      className="absolute inset-0 w-full h-full object-cover"
    />
    {/* Top + bottom scrims */}
    <div
      className="absolute inset-0"
      style={{
        background:
          "linear-gradient(180deg, rgba(15,24,52,0.35) 0%, rgba(15,24,52,0.05) 32%, rgba(15,24,52,0.10) 55%, rgba(15,24,52,0.85) 100%), linear-gradient(135deg, rgba(78,156,255,0.12) 0%, rgba(255,77,184,0.10) 60%, rgba(0,0,0,0) 100%)",
      }}
    />
    {/* Top gradient ribbon */}
    <div
      className="absolute top-0 left-0 right-0 h-1.5"
      style={{ background: "linear-gradient(90deg, #4E9CFF 0%, #D6409F 50%, #4E9CFF 100%)" }}
    />
    {/* Top row */}
    <div className="absolute top-6 sm:top-10 left-6 sm:left-12 right-6 sm:right-12 flex items-start justify-between z-10">
      <img
        src="/branding/helpgenie/helpgenie-wordmark.png"
        alt="Help Genie"
        className="h-9 sm:h-11 w-auto"
        style={{ filter: "brightness(0) invert(1) drop-shadow(0 2px 8px rgba(0,0,0,0.45))" }}
      />
      <div className="rounded-full px-4 py-1.5 text-[10px] sm:text-xs font-bold tracking-[0.18em] uppercase text-white bg-white/15 border border-white/60 backdrop-blur">
        2026 · Brochure
      </div>
    </div>

    {/* Bottom content */}
    <div className="absolute left-6 sm:left-12 right-6 sm:right-12 bottom-28 sm:bottom-32 z-10 max-w-5xl">
      <h1
        className="text-5xl sm:text-6xl lg:text-7xl xl:text-8xl font-extrabold leading-[0.98] tracking-tight text-white"
        style={{ textShadow: "0 2px 18px rgba(0,0,0,0.45)" }}
      >
        Goodbye manuals.
        <br />
        <span
          className="bg-clip-text text-transparent"
          style={{ backgroundImage: "linear-gradient(90deg, #BFE0FF 0%, #FFC0E4 100%)" }}
        >
          Hello Help Genie.
        </span>
      </h1>
      <p className="mt-6 text-lg sm:text-xl text-white/90 max-w-3xl leading-relaxed">
        The first scalable support layer for any business. One brain, trained on what you do,
        ready to answer across voice, chat, QR, and email. 24/7, in your voice.
      </p>
      <div className="mt-7 flex flex-col sm:flex-row gap-3 items-start sm:items-center">
        <button
          onClick={onStart}
          className="inline-flex items-center gap-2 px-7 py-3.5 text-white rounded-xl font-semibold shadow-lg hover:opacity-90 hover:scale-105 transition-all"
          style={{ background: "linear-gradient(90deg, #4E9CFF 0%, #FF4DB8 100%)" }}
        >
          Start the tour
          <ArrowRight className="w-4 h-4" />
        </button>
        <div className="text-xs text-white/80 inline-flex items-center gap-2 ml-1">
          <span>or use</span>
          <kbd className="px-1.5 py-0.5 rounded border border-white/30 bg-white/10 font-mono text-[10px] text-white/90">←</kbd>
          <kbd className="px-1.5 py-0.5 rounded border border-white/30 bg-white/10 font-mono text-[10px] text-white/90">→</kbd>
          <span>to navigate</span>
        </div>
      </div>
      <div className="mt-8 flex flex-wrap items-end justify-between gap-4 pt-4 border-t border-white/25 text-white/90">
        <div className="text-xs sm:text-sm">
          <div className="font-bold text-white mb-0.5">Talk to a live Help Genie</div>
          <div>{DEMO_PHONE}</div>
        </div>
        <div className="text-base sm:text-lg font-bold text-white tracking-tight">{DOMAIN}</div>
      </div>
    </div>
  </div>
);

// ─── Slide 2 — The problem ──────────────────────────────────────────────────
const ProblemSlide = () => (
  <SlideShell eyebrow="The problem" page={2} total={TOTAL_SLIDES}>
    <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center h-full">
      <div className="space-y-5">
        <Title>
          You built the business.{" "}
          <Grad>You can't be the support team too.</Grad>
        </Title>
        <Lead>
          You answer the same questions every week. You miss the 7pm caller who needed a quote.
          You watch live chat sit unanswered overnight. You hand customers a manual and hope they
          read it. Hiring a frontline rep lands at five to seven thousand dollars a month before
          they answer a single call.
        </Lead>
        <Callout>
          <strong>There is a different way to run this.</strong> The same support function, the
          same brand voice, the same answers, available on every channel your customers already
          use, day and night. At ten percent of the cost.
        </Callout>
      </div>
      <HeroImg src={ASSET("hero-trades.webp")} alt="A small-business owner working through the day knowing the genie has the phone" />
    </div>
  </SlideShell>
);

// ─── Slide 3 — The numbers (4 stat tiles) ───────────────────────────────────
const NumbersSlide = () => (
  <SlideShell eyebrow="The numbers" page={3} total={TOTAL_SLIDES}>
    <div className="space-y-6">
      <Title>
        Four realities small businesses live with,{" "}
        <Grad>whether they look at them or not.</Grad>
      </Title>
      <div className="grid grid-cols-2 gap-4 sm:gap-6">
        <StatTile big="One call" label="A missed call is usually a lost customer. Miss the 7pm quote and they've already rung the next business on the list." />
        <StatTile big="$5-7k" label="fully-loaded monthly cost of one frontline support rep. Plus the software they sit inside." />
        <StatTile big="24/7" label="hours your customers expect a response. Most small businesses cover eight of them." />
        <StatTile big="Repeats" label="The same handful of questions, asked over and over, on every channel — the repetitive load a genie absorbs so your team doesn't have to." />
      </div>
      <Callout>
        A Help Genie answers all of them, every hour of the day, in the same voice you would use
        yourself. At a fraction of what a human team costs.
      </Callout>
    </div>
  </SlideShell>
);

// ─── Slide 4 — What it actually is ──────────────────────────────────────────
const WhatSlide = () => (
  <SlideShell eyebrow="What it actually is" page={4} total={TOTAL_SLIDES}>
    <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center h-full">
      <div className="space-y-5">
        <Title>
          The first scalable support layer{" "}
          <Grad>for any business.</Grad>
        </Title>
        <Lead>
          Help Genie is an automated support layer for any business. Internal support. External
          support. The same conversational brain, trained once on what you do, answering customers
          and staff across every channel they actually use.
        </Lead>
        <Body>
          Sign up, point it at your documents and the way your business talks, and within an hour
          your business has a scalable support function. Voice. Web chat. QR codes on quotes and
          job sheets. Email. SMS. Available 24/7, in your voice, at roughly ten percent of the
          cost of building the same function with people. And once it is set up, it scales
          without limit.
        </Body>
      </div>
      <HeroImg src={ASSET("hero-home-builders.webp")} alt="A home builder handing over keys to new homeowners, supported by Help Genie" />
    </div>
  </SlideShell>
);

// ─── Slide 5 — Three pillars (infographic) ──────────────────────────────────
const PillarsSlide = () => (
  <SlideShell eyebrow="Three big bets, one tent" page={5} total={TOTAL_SLIDES}>
    <div className="space-y-6">
      <div className="space-y-3">
        <Title>
          Replace the manual.{" "}
          <Grad>Run the support function. Cover every doorway.</Grad>
        </Title>
        <Lead>
          Three pillars Help Genie covers from the day you sign up. Each one is a complete answer
          to a problem most businesses have given up trying to solve.
        </Lead>
      </div>
      <Infographic src={ASSET("infographics/p05-three-pillars.png")} alt="Three Help Genie pillars: replace the manual, run the support function, cover every doorway" />
      <div className="grid md:grid-cols-3 gap-4">
        <PillarCap title="01 · Replace the manual." body="Manuals, FAQs, install guides, warranty sheets, internal SOPs become conversations. For anyone who ships a product with a manual." />
        <PillarCap title="02 · Run the support function." body="The whole frontline support layer. Triage, knowledge retrieval, escalation. The genie is the support team. Humans only see the conversations that need them." />
        <PillarCap title="03 · Cover every doorway." body="Voice, chat, QR, email, text. Same brain, every channel. The customer who pings chat at 4pm and rings at 6pm picks up where they left off." />
      </div>
    </div>
  </SlideShell>
);

// ─── Slide 6 — One brain, every doorway ─────────────────────────────────────
const BrainSlide = () => (
  <SlideShell eyebrow="The mental model" page={6} total={TOTAL_SLIDES}>
    <div className="space-y-6">
      <div className="space-y-3">
        <Title>
          One brain. <Grad>Every doorway.</Grad>
        </Title>
        <Lead>
          Train it once on everything your business knows. Reach customers and staff wherever they
          already are. Same answers, same brand voice, same source of truth across every channel.
        </Lead>
      </div>
      <Infographic src={ASSET("infographics/p06-one-brain-many-doorways.png")} alt="Many sources of business knowledge flow into one Help Genie brain, then out across voice, chat, QR, email, and SMS doorways" />
      <Callout>
        <strong>Update once.</strong> Change the knowledge anywhere — a manual edit, a fresh FAQ,
        a price update. The genie reads the new version. The next customer gets the new answer on
        every channel.
      </Callout>
    </div>
  </SlideShell>
);

// ─── Slide 7 — How it deploys ───────────────────────────────────────────────
const DeploySlide = () => (
  <SlideShell eyebrow="How it ships" page={7} total={TOTAL_SLIDES}>
    <div className="space-y-6">
      <div className="space-y-3">
        <Title>
          Three steps. <Grad>Ten minutes.</Grad> No developer.
        </Title>
        <Lead>
          Setup is a chat conversation. That is not a marketing line. That is the actual product.
          The genie that helps you build your business is the same product your customers will
          talk to.
        </Lead>
      </div>
      <Infographic src={ASSET("infographics/p07-three-steps-deploy.png")} alt="Three-step deploy pipeline: configure the genie in chat, connect your knowledge, turn on the channels" />
      <div className="grid md:grid-cols-3 gap-4">
        <StepCap n="01" title="Configure." body="Open a chat with the genie builder. Tell it what your business does, who it serves, the tone to use." />
        <StepCap n="02" title="Connect." body="Drop in your existing materials. FAQ pages, price lists, websites, policies, manuals, SOPs. The genie ingests them." />
        <StepCap n="03" title="Channels out." body="Brand it with your colours. Turn on the channels. The genie is live on your number, website, and printed QRs within the hour." />
      </div>
      <Callout>
        <strong>No developer.</strong> No integration project. No implementation partner. Just a
        chat conversation that ends with a working support function.
      </Callout>
    </div>
  </SlideShell>
);

// ─── Slide 8 — Voice channel ────────────────────────────────────────────────
const VoiceSlide = () => (
  <ChannelSlide
    page={8}
    eyebrow="Channel one · the flagship"
    name="Voice."
    sub="Answer every call. 24/7. In your voice."
    status="live"
    statusLabel="Live now"
    lede="The voice agent picks up your business line, holds a natural conversation, books work into the calendar, and transfers to a human when it actually matters."
    bullets={[
      "Answers in under 2 seconds, every time, including 2am",
      "Books appointments straight into your calendar",
      "Transfers warm calls to your mobile when the criteria are met",
      "Every call transcribed and tagged so you see what is coming in",
      "Bring your existing number or get a new one. Local or toll-free.",
    ]}
    replaces="the receptionist you cannot afford to hire, the voicemail nobody listens back to, the lost-after-hours column on your spreadsheet."
    tryLine="Call the live demo and ask the genie anything about Help Genie."
    tryNumber={DEMO_PHONE}
    heroSrc={ASSET("hero-automotive.webp")}
    heroAlt="A customer learning about a car at a dealership, supported by a Help Genie on the phone"
  />
);

// ─── Slide 9 — Chat + QR ────────────────────────────────────────────────────
const ChatQRSlide = () => (
  <ChannelSlide
    page={9}
    eyebrow="Channel two · embed and print"
    name="Chat. QR."
    sub="Drop the genie on your website. Print it on every job sheet."
    status="live"
    statusLabel="Live now"
    lede="Same brain, two more doorways. Embed the chat widget on any website with a snippet. Print a QR on quotes, invoices, fridge magnets, job sheets, hotel keycards. Customers scan, land on a mobile chat with the genie. No app install."
    bullets={[
      "Web chat widget embedded with one JavaScript snippet",
      "Branded to your site. Same brain as the voice agent.",
      "QR codes generated in the product. Print them anywhere.",
      "Hands off to a human the moment the conversation calls for one",
      "Continues the conversation if the customer moves from chat to phone",
    ]}
    replaces="the FAQ page nobody reads, the contact form that lands in a folder, the support email that takes four days to get a reply."
    tryLine="Scan a Help Genie QR or visit the live demo."
    tryNumber={DEMO_WEB_DISPLAY}
    heroSrc={ASSET("hero-office-equipment.webp")}
    heroAlt="An office worker scanning a QR code on a printer to talk to a Help Genie"
  />
);

// ─── Slide 10 — Use case · external (customers) ─────────────────────────────
const ExternalSupportSlide = () => (
  <UseCaseSlide
    page={10}
    eyebrow="Use case · customer support"
    titleA="External support,"
    titleB="on every channel, 24/7."
    lede="The customer-facing support layer. Voice, chat, QR, email. The genie answers questions, books work, captures leads, handles complaints, and escalates to a human when the conversation calls for one."
    bullets={[
      "Answers product questions from the manual, FAQ, and pricing pages",
      "Books urgent jobs into the calendar at 11pm",
      "Qualifies leads on the after-hours web chat and pushes them to your CRM",
      "Captures complaints in structured form, routed to the right human",
      "Triages volume so the team only sees what needs them",
    ]}
    example="A trades business handed the after-hours line to a Help Genie. In month one, the genie handled dozens of calls between 5pm and 8am. Urgent work got booked overnight. The rest got a callback the next morning. Zero rang the competitor."
    heroSrc={ASSET("hero-marine.webp")}
    heroAlt="A marina manager checking the Help Genie on their phone at a golden-hour dock"
  />
);

// ─── Slide 11 — Use case · internal (staff) ─────────────────────────────────
const InternalSupportSlide = () => (
  <UseCaseSlide
    page={11}
    eyebrow="Use case · internal support"
    titleA="Internal support."
    titleB="Your team's first call."
    lede="Every business has a second pile of repeat questions: the ones the team asks each other. Where is the X form. How do we handle Y. What is our policy on Z. A Help Genie answers them from your handbook, SOPs, and internal procedures, on a phone or a chat, on the field or at the desk."
    bullets={[
      "Trained on your employee handbook, SOPs, and internal procedures",
      "Available to staff on a private phone line, web chat, or QR sticker",
      "Field teams ring the genie for parts, codes, callbacks, install steps",
      "New hires ramp without burning a senior on the same questions",
      "When you change a procedure, the genie picks up the new answer",
    ]}
    example="A growing trades company put a genie on a private number for its field teams. Apprentices ring it from the van for spec lookups instead of pulling a senior off a job. The same genie now handles HR questions for the office."
    heroSrc={ASSET("hero-industrial.webp")}
    heroAlt="A safety manager at an industrial facility using a Help Genie for procedure lookups"
  />
);

// ─── Slide 12 — Manufacturers ────────────────────────────────────────────────
const ManufacturersSlide = () => (
  <UseCaseSlide
    page={12}
    eyebrow="Use case · manufacturers and product companies"
    titleA="Your customers"
    titleB="do not read the manual. They would ask the product."
    lede="Every product ships with documentation customers never read. Install guides, warranty terms, troubleshooting flows, parts diagrams. Put a Help Genie on it. The product becomes talkable. Customers ask the genie and the genie answers from the actual source of truth."
    bullets={[
      "Point the genie at the install guide, owner manual, spec sheet, FAQ",
      "Print a QR on the box, the product, the warranty card, the welcome pack",
      "Customers ask in voice or chat. The genie answers in plain language.",
      "Service questions get triaged. Warranty cases get routed to the right team.",
      "Update the source. Every customer gets the new answer instantly.",
    ]}
    example="An equipment maker put a Help Genie on a sticker inside the door of every unit they ship. Customers scan to ask anything. Service calls dropped because the easy questions stopped reaching the phone."
    heroSrc={ASSET("hero-appliances.webp")}
    heroAlt="An appliance showroom representative supported by a Help Genie"
  />
);

// ─── Slide 13 — Economics ───────────────────────────────────────────────────
const EconomicsSlide = () => (
  <SlideShell eyebrow="The economics" page={13} total={TOTAL_SLIDES}>
    <div className="space-y-6">
      <div className="space-y-3">
        <Title>
          A support function <Grad>at ten percent of the cost.</Grad>
        </Title>
        <Lead>
          The hire you cannot justify becomes the subscription you can. And once Help Genie is
          configured, it scales without hiring anyone else.
        </Lead>
      </div>
      <div className="grid md:grid-cols-2 gap-5">
        <EconCard
          label="One frontline rep"
          big="$5–7k"
          gradient={false}
          sub="Fully-loaded monthly cost of one person on the phones."
          rows={[
            { mark: "$", title: "Salary, tax, super.", body: "One person, 40 hours, five days a week." },
            { mark: "⌘", title: "Software stack.", body: "Phone system, CRM, ticketing, headset, training." },
            { mark: "⌛", title: "Time to onboard.", body: "Weeks before they can answer unaided." },
            { mark: "✕", title: "Sick days, holidays, churn.", body: "Coverage gaps, recruitment, re-training." },
          ]}
          scaleBy="Hiring another person"
        />
        <EconCard
          label="One Help Genie"
          big="~10%"
          gradient
          sub="Of that cost. Voice, chat, QR, email. 24/7. In your voice."
          rows={[
            { mark: "●", title: "Flat monthly.", body: "Genie, knowledge base, every channel, all included." },
            { mark: "∞", title: "Unlimited parallel conversations.", body: "50 callers at once, same brain." },
            { mark: "⏱", title: "24/7, instant pickup.", body: "Under two seconds, every time, including 2am." },
            { mark: "✓", title: "Never sick, never churns.", body: "Same answers, every time, every channel." },
          ]}
          scaleBy="Flipping a setting"
        />
      </div>
      <Callout>
        <strong>And it scales.</strong> The genie that handles 50 conversations a day handles
        5,000 the same way. Adding capacity is a setting, not a hire.
      </Callout>
    </div>
  </SlideShell>
);

// ─── Slide 14 — Control panel ───────────────────────────────────────────────
const ControlPanelSlide = () => (
  <SlideShell eyebrow="The control panel" page={14} total={TOTAL_SLIDES}>
    <div className="grid lg:grid-cols-[1fr_1.3fr] gap-6 lg:gap-10 items-start">
      <div className="space-y-4">
        <Title>
          One screen.{" "}
          <Grad>Every conversation, every channel, every genie.</Grad>
        </Title>
        <Lead>
          You watch what the genie is doing the way you would watch a team. Conversations, leads,
          analytics, brand, escalations. All in one place.
        </Lead>
        <div className="rounded-2xl overflow-hidden h-32 sm:h-40">
          <img
            src={ASSET("cap-insights.webp")}
            alt="An operator reviewing the Help Genie analytics dashboard on a laptop"
            className="w-full h-full object-cover"
          />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3 sm:gap-4">
        <PanelTile img={ASSET("panel-genies.png")} title="Your genies." body="Every genie you have spun up. Status, channels, voice, owner." />
        <PanelTile img={ASSET("panel-analytics.png")} title="Analytics + insights." body="What customers ask. Where conversations drop. What is driving outcomes." />
        <PanelTile img={ASSET("panel-conversations.png")} title="Conversations + leads." body="Every conversation across every channel. Qualified leads, booked work, callbacks." />
        <PanelTile img={ASSET("panel-genie-config.png")} title="Brand + playbooks." body="Lock the voice, the tone, the escalation rules. Reusable playbooks per use case." />
      </div>
    </div>
  </SlideShell>
);

// ─── Slide 15 — Programmatic platform ───────────────────────────────────────
const PlatformSlide = () => (
  <SlideShell eyebrow="The platform underneath" page={15} total={TOTAL_SLIDES}>
    <div className="grid lg:grid-cols-[1fr_1fr] gap-6 lg:gap-10 items-center">
      <div className="space-y-4">
        <Title>
          Completely programmatic. <Grad>Build on top.</Grad>
        </Title>
        <Lead>
          Help Genie is not a closed box. Every surface in the product has an API behind it. Push
          knowledge in. Pull conversations out. Wire the genie into anything else you run.
        </Lead>
        <div className="grid sm:grid-cols-2 gap-3">
          <ProgCap title="Public API." body="30+ resources. Everything the UI does, the API does." />
          <ProgCap title="Dynamic ingest." body="Push documents, sync from sheets, schedule scrapes. The genie answers from the latest version." />
          <ProgCap title="Smart recipes." body="Paste an API key. Describe the action. The platform builds a reusable HTTP recipe." />
          <ProgCap title="Webhooks + events." body="Every call, chat, lead, escalation fires an event. Wire it in. Build on top." />
        </div>
        <Callout>
          <strong>Vibe-code your support function.</strong> Most customers never touch the API.
          The teams that do build entire bespoke flows on top of Help Genie in a day, with nothing
          but a chat window and the docs.
        </Callout>
      </div>
      <div className="flex items-center justify-center">
        <img
          src={ASSET("infographics/p15-programmatic-platform.png")}
          alt="Help Genie platform stack: public API, dynamic knowledge ingest, smart integration recipes, and webhooks fanning out to connected services"
          className="w-full max-w-md max-h-[60vh] object-contain rounded-2xl"
        />
      </div>
    </div>
  </SlideShell>
);

// ─── Slide 16 — Mail ────────────────────────────────────────────────────────
const MailSlide = () => (
  <ChannelSlide
    page={16}
    eyebrow="Email · your genie's own inbox"
    name="Mail."
    sub="Your support email, taken care of."
    status="live"
    statusLabel="Live"
    lede="Every genie comes with its own email address. Point your support address at it and the same brain that runs voice and chat reads every email, answers it from your knowledge base, and forwards you only the ones it is not sure about."
    bullets={[
      "Its own address the moment the genie is created, nothing to connect",
      "Answers in your brand voice, grounded in your knowledge base",
      "Escalates to you by email when confidence is low",
      "Every reply logged with the reasoning behind it",
      "Test mode so you can watch it work before it goes live",
    ]}
    replaces="the inbox that never empties, the customer who waited four days for an answer that was already in the FAQ, the team member who reads 200 emails to find the 5 that matter."
    tryLine="Email the demo genie and watch it answer."
    tryNumber={FOUNDER_EMAIL}
    heroSrc={ASSET("cap-qualification.webp")}
    heroAlt="An operator reviewing a printed FAQ next to a laptop running Help Genie Mail"
  />
);

// ─── Slide 17 — Sync + Outbound ─────────────────────────────────────────────
const SyncOutboundSlide = () => (
  <SlideShell eyebrow="Sync and Outbound" page={17} total={TOTAL_SLIDES}>
    <div className="space-y-6">
      <div className="space-y-3">
        <Title>
          Sync. <Grad>Outbound.</Grad>
        </Title>
        <StatusPill type="live">Live</StatusPill>
      </div>
      <div className="rounded-2xl overflow-hidden h-44 sm:h-52">
        <img src={ASSET("card-insights.webp")} alt="Help Genie Sync and Outbound feeding live insights back to the business" className="w-full h-full object-cover" />
      </div>
      <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
        <div className="space-y-3">
          <div className="text-xl font-bold text-[#28406F]">Sync.</div>
          <Body>
            The integration layer for the genie. After a call, chat, or email, Sync writes the
            right thing to the right system, no retyping.
          </Body>
          <Bullets
            items={[
              "700+ pre-built integrations: HubSpot, Salesforce, Pipedrive, Google Calendar, Outlook, ServiceTitan, Slack, and more",
              "Bring your own API for anything custom. The platform designs the recipe.",
              "Mid-conversation lookups: check stock, availability, or a customer record live on the call",
              "Post-conversation actions: book the job, send the quote, log the disposition, ping the right Slack channel",
            ]}
          />
        </div>
        <div className="space-y-3">
          <div className="text-xl font-bold text-[#28406F]">Outbound.</div>
          <Body>
            Outbound calling for sales, follow-ups, win-back, qualification, and NPS surveys. The
            genie places the call, holds the conversation, captures the result.
          </Body>
          <Bullets
            items={[
              "Campaign builder with target lists from your CRM",
              "Disposition framework: answered, voicemail, callback, qualified, not qualified, do-not-call",
              "TCPA and DNC compliance built in",
              "Live dashboards: connect rate, conversation length, qualified-per-hour",
            ]}
          />
        </div>
      </div>
      <Callout>
        <strong>Included with the core.</strong> Both come with Help Genie for Business. Same
        genie, same knowledge, one login.
      </Callout>
    </div>
  </SlideShell>
);

// ─── Slide 18 — Help Genie Mobile ───────────────────────────────────────────
const MobileSlide = () => (
  <SlideShell eyebrow="Help Genie · in your pocket" page={18} total={TOTAL_SLIDES}>
    <div className="space-y-6">
      <div className="space-y-3">
        <Title>
          The whole genie. <Grad>On your phone.</Grad>
        </Title>
        <Lead>
          Every Help Genie comes with a native iOS and Android app for owners, operators, and end
          customers. Spin up genies, watch conversations live, hand off to a human, train on the
          fly. The control panel that lives at your desk also lives in your pocket.
        </Lead>
      </div>
      <div className="grid lg:grid-cols-2 gap-5">
        <div
          className="rounded-2xl border p-4 sm:p-5 grid grid-cols-3 gap-3 items-end"
          style={{ borderColor: "#E5EAF0", background: "linear-gradient(135deg, rgba(78,156,255,0.10), rgba(255,77,184,0.10))" }}
        >
          <PhoneShot src={ASSET("mobile-welcome.png")} alt="Help Genie mobile app welcome screen" />
          <PhoneShot src={ASSET("mobile-my-genies.png")} alt="Help Genie mobile app My Genies list" lift />
          <PhoneShot src={ASSET("mobile-conversation.png")} alt="Help Genie mobile app start-conversation screen" />
        </div>
        <div className="rounded-2xl overflow-hidden h-56 sm:h-full min-h-[14rem]">
          <img
            src={ASSET("cap-routing.webp")}
            alt="A customer in a modern office with a Help Genie running live on his phone"
            className="w-full h-full object-cover"
          />
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <MobileCell title="Owner cockpit." body="Live conversations, today's bookings, escalations, lead notifications. The genie pings you when it actually needs you." />
        <MobileCell title="Operator inbox." body="Pick up escalated chats, reply by voice or type, hand back to the genie when it's resolved. No new tool, no new tab." />
        <MobileCell title="Build on the move." body="Spin up a new genie from a phone in the same chat conversation as the desktop. Quote a new client, walk out with a working genie." />
        <MobileCell title="For your customers, too." body="A consumer marketplace where your customers add the genies they actually use. Branded, owned by you, distributed everywhere." />
      </div>
    </div>
  </SlideShell>
);

// ─── Slide 19 — Industries (12 verticals) ───────────────────────────────────
const VERTICALS = [
  { name: "Trades", line: "Books jobs and handles calls while you work." },
  { name: "Automotive", line: "Drives sales and service excellence." },
  { name: "Marine", line: "Navigates customer engagement." },
  { name: "Appliances", line: "Powers up customer service." },
  { name: "Office Equipment", line: "Keeps your office running smoothly." },
  { name: "Home Builders", line: "Supports homeowners from build to living." },
  { name: "Travel & Hospitality", line: "Elevates every guest experience." },
  { name: "Manufacturing", line: "Streamlines operations, boosts productivity." },
  { name: "Industrial", line: "Keeps operations running 24/7." },
  { name: "Mining", line: "Keeps mines running 24/7." },
  { name: "Real Estate", line: "Never misses a listing enquiry." },
  { name: "Events", line: "Makes every event unforgettable." },
];

const IndustriesSlide = () => (
  <SlideShell eyebrow="Industry fit" page={19} total={TOTAL_SLIDES}>
    <div className="space-y-6">
      <div className="space-y-3">
        <Title>
          Built for businesses that{" "}
          <Grad>answer the same questions every day.</Grad>
        </Title>
        <Lead>
          Twelve verticals we ship templates and playbooks for today. The pattern repeats: high
          volume of repeat questions, customers reaching out across more than one channel, not
          enough hours in the day. If your business is on this list, a genie has been built for
          it. If it is not, we will build one for it.
        </Lead>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
        {VERTICALS.map((v) => (
          <div
            key={v.name}
            className="relative rounded-xl bg-white border border-[#E5EAF0] p-4 overflow-hidden"
          >
            <div
              className="absolute top-0 left-0 right-0 h-[3px]"
              style={{ background: "linear-gradient(90deg, #4E9CFF 0%, #FF4DB8 100%)" }}
            />
            <div className="text-sm sm:text-base font-bold text-[#28406F] mt-1">{v.name}</div>
            <div className="text-xs sm:text-sm text-gray-600 mt-1 leading-snug">{v.line}</div>
          </div>
        ))}
      </div>
      <Callout>
        <strong>And the next twelve.</strong> Equipment, energy, healthcare, education, fitness,
        hospitality, professional services, government, retail, logistics. Help Genie fits any
        business where the same questions get asked twice.
      </Callout>
    </div>
  </SlideShell>
);

// ─── Slide 20 — Get started ─────────────────────────────────────────────────
const GetStartedSlide = () => (
  <SlideShell eyebrow="Get started" page={20} total={TOTAL_SLIDES}>
    <div className="space-y-6">
      <div className="space-y-3">
        <Title>
          Three doorways. <Grad>Pick whichever is easiest.</Grad>
        </Title>
        <Lead>
          The fastest way to know if Help Genie fits is to use one. Ring the genie that answers
          our own line, talk to one online, or email Sam directly. Every doorway lands you in a
          real conversation, not a marketing demo.
        </Lead>
      </div>

      {/* What you actually get */}
      <div className="p-[2px] rounded-2xl" style={{ background: "linear-gradient(90deg, #4E9CFF 0%, #FF4DB8 100%)" }}>
        <div className="rounded-[14px] bg-white p-6 sm:p-7">
          <div className="text-xs font-bold uppercase tracking-[0.18em] text-[#28406F] mb-4">
            What you actually get
          </div>
          <div className="grid sm:grid-cols-2 gap-x-8 gap-y-3">
            {[
              ["The genie.", "Voice, chat, and QR on day one. Same brain across every channel."],
              ["Your knowledge base.", "Pages, FAQs, manuals, prices, SOPs, all ingested."],
              ["The control panel.", "Conversations, leads, analytics, brand controls in one screen."],
              ["The platform.", "Public API, webhooks, smart integration recipes."],
              ["Onboarding.", "Knowledge in, genie configured, integrations wired, live in days."],
              ["Mail, Sync, Outbound.", "Live and included. Same genie, one login."],
            ].map(([title, body]) => (
              <div key={title} className="flex gap-3 text-sm text-gray-700">
                <span
                  className="mt-2 flex-shrink-0 h-2 w-2 rounded-full"
                  style={{ background: "linear-gradient(135deg, #4E9CFF 0%, #FF4DB8 60%, #FFBB33 100%)" }}
                />
                <span>
                  <strong className="text-[#28406F] font-bold">{title}</strong> {body}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        <Doorway n="01" title="Call the live demo." big={DEMO_PHONE} body="Ring the genie that answers our own line. Ask it anything." href={`tel:${DEMO_PHONE.replace(/\s+/g, "")}`} />
        <Doorway n="02" title="Talk to one online." big={DEMO_WEB_DISPLAY} bigSize="sm" body="Chat with the live agent. See the conversation flow end to end." href={DEMO_WEB_HREF} />
        <Doorway n="03" title="Email the founder." big={FOUNDER_EMAIL} bigSize="sm" body="Tell Sam about your business. He reads every email." href={`mailto:${FOUNDER_EMAIL}`} />
      </div>
    </div>
  </SlideShell>
);

// ─── Slide 21 — Back cover (image-led, "Now we're talking") ─────────────────
const BackCover = ({ onExit }: { onExit: () => void }) => (
  <div className="relative h-full w-full overflow-hidden">
    <img
      src={ASSET("usecase-support.webp")}
      alt="A customer at his kitchen table running a branded Help Genie on his phone"
      className="absolute inset-0 w-full h-full object-cover"
    />
    <div
      className="absolute inset-0"
      style={{
        background:
          "linear-gradient(180deg, rgba(28,37,71,0.55) 0%, rgba(28,37,71,0.40) 35%, rgba(28,37,71,0.78) 100%), linear-gradient(135deg, rgba(78,156,255,0.30) 0%, rgba(255,77,184,0.35) 100%)",
      }}
    />
    <div className="absolute top-0 left-0 right-0 h-2 bg-white/40" />

    {/* Top wordmark */}
    <div className="absolute top-10 sm:top-14 left-6 sm:left-12 z-10">
      <img
        src="/branding/helpgenie/helpgenie-wordmark.png"
        alt="Help Genie"
        className="h-10 sm:h-14 w-auto"
        style={{ filter: "brightness(0) invert(1)" }}
      />
    </div>

    {/* Middle tagline */}
    <div className="absolute inset-0 flex items-center px-6 sm:px-12 z-10">
      <div className="max-w-5xl">
        <h1 className="text-white font-extrabold tracking-tight leading-[0.96] text-6xl sm:text-7xl lg:text-8xl xl:text-9xl">
          Now we're
          <br />
          talking.
        </h1>
        <p className="mt-6 text-xl sm:text-2xl text-white/95 font-medium">
          Real world support. Reinvented.
        </p>
        <div className="mt-7 h-1.5 w-40 rounded-full bg-white/90" />
        <div className="mt-7 text-2xl sm:text-3xl font-bold text-white tracking-tight">{DOMAIN}</div>
      </div>
    </div>

    {/* Bottom row */}
    <div className="absolute bottom-28 sm:bottom-32 left-6 sm:left-12 right-6 sm:right-12 z-10 flex flex-col sm:flex-row items-start sm:items-end justify-between gap-4">
      <div className="text-sm sm:text-base text-white/95 leading-relaxed" style={{ textShadow: "0 1px 6px rgba(0,0,0,0.45)" }}>
        <div>{DEMO_PHONE}</div>
        <div><CopyableEmail email={FOUNDER_EMAIL} /></div>
      </div>
      <button
        onClick={onExit}
        className="inline-flex items-center gap-2 px-5 py-3 text-sm font-semibold rounded-xl text-white bg-white/15 border border-white/40 backdrop-blur hover:bg-white/25 transition-all"
      >
        Back to my dashboard
        <ArrowRight className="w-4 h-4" />
      </button>
    </div>

    {/* Mascot */}
    <img
      src="/branding/helpgenie/mascot-large.png"
      alt="Help Genie mascot"
      className="absolute -bottom-10 right-2 sm:right-8 w-52 sm:w-64 lg:w-72 z-0 opacity-95 pointer-events-none drop-shadow-2xl"
      onError={(e) => {
        (e.currentTarget as HTMLImageElement).src = "/branding/helpgenie/mascot.png";
      }}
    />
  </div>
);

// ─── Helpers ────────────────────────────────────────────────────────────────
const HeroImg = ({ src, alt }: { src: string; alt: string }) => (
  <div className="rounded-2xl overflow-hidden bg-[#F4F6FB] h-60 sm:h-80 lg:h-[28rem] shadow-lg">
    <img src={src} alt={alt} className="w-full h-full object-cover" />
  </div>
);

const Infographic = ({ src, alt }: { src: string; alt: string }) => (
  <div className="rounded-2xl overflow-hidden bg-white flex items-center justify-center">
    <img
      src={src}
      alt={alt}
      className="w-auto max-w-full max-h-[38vh] object-contain"
    />
  </div>
);

const StatTile = ({ big, label }: { big: string; label: string }) => (
  <div
    className="rounded-2xl p-5 sm:p-7 border-[1.5px]"
    style={{
      background: "linear-gradient(#FFFFFF, #FFFFFF) padding-box, linear-gradient(90deg, #4E9CFF 0%, #D6409F 50%, #4E9CFF 100%) border-box",
      borderColor: "transparent",
    }}
  >
    <div
      className="text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-none bg-clip-text text-transparent"
      style={{ backgroundImage: "linear-gradient(90deg, #4E9CFF 0%, #D6409F 50%, #4E9CFF 100%)" }}
    >
      {big}
    </div>
    <div className="mt-3 text-sm sm:text-base text-gray-700 leading-relaxed">{label}</div>
  </div>
);

const PillarCap = ({ title, body }: { title: string; body: string }) => (
  <div
    className="p-4 sm:p-5 rounded-xl bg-[#F4F6FB] border-t-[3px]"
    style={{ borderImage: "linear-gradient(90deg, #4E9CFF 0%, #FF4DB8 100%) 1", borderTopWidth: 3 }}
  >
    <div className="text-sm font-bold text-[#28406F] mb-1.5">{title}</div>
    <div className="text-sm text-gray-700 leading-relaxed">{body}</div>
  </div>
);

const StepCap = ({ n, title, body }: { n: string; title: string; body: string }) => (
  <div className="p-4 sm:p-5 rounded-xl bg-[#F4F6FB]">
    <div className="text-sm font-bold text-[#28406F]">
      {n} · {title}
    </div>
    <div className="text-sm text-gray-700 mt-1.5 leading-relaxed">{body}</div>
  </div>
);

const ProgCap = ({ title, body }: { title: string; body: string }) => (
  <div
    className="px-4 py-3 rounded-r-lg bg-[#F4F6FB] border-l-[3px]"
    style={{ borderImage: "linear-gradient(180deg, #4E9CFF 0%, #FF4DB8 100%) 1", borderLeftWidth: 3 }}
  >
    <span className="font-bold text-[#28406F]">{title}</span>{" "}
    <span className="text-sm text-gray-700">{body}</span>
  </div>
);

const PanelTile = ({ img, title, body }: { img: string; title: string; body: string }) => (
  <div className="relative rounded-xl overflow-hidden border border-[#E5EAF0] bg-white flex flex-col">
    <div
      className="absolute top-0 left-0 right-0 h-[3px] z-10"
      style={{ background: "linear-gradient(90deg, #4E9CFF 0%, #FF4DB8 100%)" }}
    />
    <div
      className="p-3 sm:p-4 flex items-center justify-center"
      style={{ background: "linear-gradient(135deg, rgba(78,156,255,0.06), rgba(255,77,184,0.04))" }}
    >
      <img src={img} alt={title} className="h-20 sm:h-24 lg:h-28 w-auto rounded shadow-sm" />
    </div>
    <div className="px-3 py-2.5 sm:px-4 sm:py-3 border-t border-[#E5EAF0]">
      <div className="text-sm font-bold text-[#28406F] leading-tight">{title}</div>
      <div className="text-xs text-gray-700 leading-snug mt-0.5">{body}</div>
    </div>
  </div>
);

const PhoneShot = ({ src, alt, lift = false }: { src: string; alt: string; lift?: boolean }) => (
  <div
    className={`rounded-2xl overflow-hidden bg-white shadow-xl ${lift ? "-translate-y-4 shadow-2xl" : ""}`}
  >
    <img src={src} alt={alt} className="w-full h-auto object-cover" />
  </div>
);

const MobileCell = ({ title, body }: { title: string; body: string }) => (
  <div className="relative rounded-xl bg-white border border-[#E5EAF0] p-4 sm:p-5">
    <div
      className="absolute top-0 left-0 right-0 h-[3px] rounded-t-xl"
      style={{ background: "linear-gradient(90deg, #4E9CFF 0%, #FF4DB8 100%)" }}
    />
    <div className="text-sm sm:text-base font-bold text-[#28406F] mt-1">{title}</div>
    <div className="text-xs sm:text-sm text-gray-700 mt-1.5 leading-relaxed">{body}</div>
  </div>
);

const StatusPill = ({ type, children }: { type: "live" | "beta"; children: ReactNode }) => {
  if (type === "live") {
    return (
      <span className="inline-flex items-center gap-2 text-xs font-bold tracking-[0.12em] uppercase text-[#4E9CFF] bg-[#4E9CFF]/10 border border-[#4E9CFF]/40 rounded-full px-3 py-1">
        <span className="w-1.5 h-1.5 rounded-full bg-[#4E9CFF] shadow-[0_0_6px_#4E9CFF]" />
        {children}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-2 text-xs font-bold tracking-[0.12em] uppercase text-[#B57A00] bg-[#FFBB33]/15 border border-[#FFBB33]/50 rounded-full px-3 py-1">
      {children}
    </span>
  );
};

// Channel page builder (Voice, Chat+QR, Mail)
const ChannelSlide = ({
  page,
  eyebrow,
  name,
  sub,
  status,
  statusLabel,
  lede,
  bullets,
  replaces,
  tryLine,
  tryNumber,
  heroSrc,
  heroAlt,
}: {
  page: number;
  eyebrow: string;
  name: string;
  sub: string;
  status: "live" | "beta";
  statusLabel: string;
  lede: string;
  bullets: string[];
  replaces: string;
  tryLine: string;
  tryNumber: string;
  heroSrc: string;
  heroAlt: string;
}) => (
  <SlideShell eyebrow={eyebrow} page={page} total={TOTAL_SLIDES}>
    <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-start h-full">
      <div className="space-y-5">
        <div>
          <div
            className="text-5xl sm:text-6xl lg:text-7xl font-extrabold leading-[0.95] tracking-tight bg-clip-text text-transparent"
            style={{ backgroundImage: "linear-gradient(90deg, #4E9CFF 0%, #D6409F 50%, #4E9CFF 100%)" }}
          >
            {name}
          </div>
          <div className="text-base sm:text-lg font-semibold text-[#28406F] mt-2">{sub}</div>
          <div className="mt-3">
            <StatusPill type={status}>{statusLabel}</StatusPill>
          </div>
        </div>
        <Lead>{lede}</Lead>
        <Bullets items={bullets} />
        <div
          className="rounded-xl px-5 py-4 text-sm text-gray-700"
          style={{ background: "linear-gradient(135deg, rgba(78,156,255,0.08) 0%, rgba(255,77,184,0.08) 100%)" }}
        >
          <strong className="text-[#28406F]">Replaces:</strong> {replaces}
        </div>
        <div className="rounded-xl px-5 py-4 text-sm text-white" style={{ background: "#28406F" }}>
          <strong className="text-white">Try it now.</strong> {tryLine}
          <div className="mt-2 text-xl sm:text-2xl font-extrabold tracking-tight">{tryNumber}</div>
        </div>
      </div>
      <div className="rounded-2xl overflow-hidden h-64 sm:h-80 lg:h-[28rem] shadow-lg">
        <img src={heroSrc} alt={heroAlt} className="w-full h-full object-cover" />
      </div>
    </div>
  </SlideShell>
);

// Use-case slide builder
const UseCaseSlide = ({
  page,
  eyebrow,
  titleA,
  titleB,
  lede,
  bullets,
  example,
  heroSrc,
  heroAlt,
}: {
  page: number;
  eyebrow: string;
  titleA: string;
  titleB: string;
  lede: string;
  bullets: string[];
  example: string;
  heroSrc: string;
  heroAlt: string;
}) => (
  <SlideShell eyebrow={eyebrow} page={page} total={TOTAL_SLIDES}>
    <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-start h-full">
      <div className="space-y-5">
        <Title>
          {titleA} <Grad>{titleB}</Grad>
        </Title>
        <Lead>{lede}</Lead>
        <Bullets items={bullets} />
        <Callout>
          <strong>What it looks like.</strong> {example}
        </Callout>
      </div>
      <HeroImg src={heroSrc} alt={heroAlt} />
    </div>
  </SlideShell>
);

// Economics card
const EconCard = ({
  label,
  big,
  gradient,
  sub,
  rows,
  scaleBy,
}: {
  label: string;
  big: string;
  gradient: boolean;
  sub: string;
  rows: { mark: string; title: string; body: string }[];
  scaleBy: string;
}) => (
  <div
    className={`rounded-2xl p-6 sm:p-7 bg-white ${gradient ? "" : "border border-[#E5EAF0]"}`}
    style={
      gradient
        ? {
            background:
              "linear-gradient(#FFFFFF, #FFFFFF) padding-box, linear-gradient(90deg, #4E9CFF 0%, #D6409F 50%, #4E9CFF 100%) border-box",
            border: "1.5px solid transparent",
          }
        : undefined
    }
  >
    <div className="text-[10px] sm:text-xs font-bold tracking-[0.18em] uppercase text-[#64748B]">
      {label}
    </div>
    <div
      className={`mt-1 text-5xl sm:text-6xl font-extrabold leading-none ${gradient ? "bg-clip-text text-transparent" : "text-[#28406F]"}`}
      style={
        gradient
          ? { backgroundImage: "linear-gradient(135deg, #4E9CFF 0%, #FF4DB8 60%, #FFBB33 100%)" }
          : undefined
      }
    >
      {big}
    </div>
    <div className="mt-2 text-sm text-gray-700">{sub}</div>
    <ul className="mt-4 space-y-3">
      {rows.map((r) => (
        <li key={r.title} className="flex gap-3">
          <span
            className={`flex-shrink-0 inline-flex items-center justify-center h-7 w-7 rounded-full text-xs font-bold ${gradient ? "text-[#4E9CFF]" : "text-[#28406F]"}`}
            style={
              gradient
                ? { background: "linear-gradient(135deg, rgba(78,156,255,0.15), rgba(255,77,184,0.15))", border: "1.5px solid rgba(78,156,255,0.4)" }
                : { background: "#F4F6FB", border: "1.5px solid #E5EAF0" }
            }
          >
            {r.mark}
          </span>
          <div className="text-sm text-gray-700 leading-relaxed">
            <strong className="text-[#28406F]">{r.title}</strong> {r.body}
          </div>
        </li>
      ))}
    </ul>
    <div className="mt-5 pt-4 border-t border-dashed border-[#E5EAF0] flex items-center justify-between text-xs text-[#64748B]">
      <span className="uppercase tracking-[0.18em]">Scales by</span>
      <strong className="text-[#28406F]">{scaleBy}</strong>
    </div>
  </div>
);

const Doorway = ({
  n,
  title,
  big,
  body,
  href,
  bigSize = "md",
}: {
  n: string;
  title: string;
  big: string;
  body: string;
  href: string;
  bigSize?: "sm" | "md";
}) => (
  <a
    href={href}
    target={href.startsWith("http") ? "_blank" : undefined}
    rel={href.startsWith("http") ? "noopener noreferrer" : undefined}
    className="group block p-[2px] rounded-2xl hover:shadow-xl transition-shadow"
    style={{ background: "linear-gradient(90deg, #4E9CFF 0%, #FF4DB8 100%)" }}
  >
    <div className="rounded-[14px] bg-white p-5 sm:p-6 h-full flex flex-col gap-2">
      <div
        className="text-2xl font-extrabold leading-none bg-clip-text text-transparent"
        style={{ backgroundImage: "linear-gradient(135deg, #4E9CFF 0%, #FF4DB8 60%, #FFBB33 100%)" }}
      >
        {n}
      </div>
      <div className="text-sm font-bold text-[#28406F]">{title}</div>
      <div
        className={`font-extrabold text-[#28406F] tracking-tight break-all ${bigSize === "sm" ? "text-sm sm:text-base" : "text-lg sm:text-xl"}`}
      >
        {big}
      </div>
      <div className="text-xs sm:text-sm text-gray-700 leading-relaxed">{body}</div>
    </div>
  </a>
);

// ─── Slide list ─────────────────────────────────────────────────────────────
export interface SlideRenderProps {
  onStart: () => void;
  onExit: () => void;
}

export const renderSlide = (index: number, { onStart, onExit }: SlideRenderProps) => {
  switch (index) {
    case 0: return <Cover onStart={onStart} />;
    case 1: return <ProblemSlide />;
    case 2: return <NumbersSlide />;
    case 3: return <WhatSlide />;
    case 4: return <PillarsSlide />;
    case 5: return <BrainSlide />;
    case 6: return <DeploySlide />;
    case 7: return <VoiceSlide />;
    case 8: return <ChatQRSlide />;
    case 9: return <ExternalSupportSlide />;
    case 10: return <InternalSupportSlide />;
    case 11: return <ManufacturersSlide />;
    case 12: return <EconomicsSlide />;
    case 13: return <ControlPanelSlide />;
    case 14: return <PlatformSlide />;
    case 15: return <MailSlide />;
    case 16: return <SyncOutboundSlide />;
    case 17: return <MobileSlide />;
    case 18: return <IndustriesSlide />;
    case 19: return <GetStartedSlide />;
    case 20: return <BackCover onExit={onExit} />;
    default: return null;
  }
};
